import { Award } from 'lucide-react';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactTable from 'react-table';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Row
} from 'reactstrap';
import { Colxx } from '../../../components/common/CustomBootstrap';
import StatCard from '../../../components/dashboard/StatCard';
import DataTablePagination from '../../../components/DatatablePagination';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import Customer from '../CRM/components/Customer';

const TRANSACTION_COLORS = {
  credit: 'primary',
  cashback: 'warning',
  voucher: 'info',
  earning: 'success',
  expense: 'danger',
  refund: 'secondary',
  bogo: 'dark',
  compensation: 'primary',
};

// --- CalculatePoints per user (mirrors cloud function) ---
const calculatePointsForUser = (logs) => {
  const sorted = [...logs].sort((a, b) => (a.created || 0) - (b.created || 0));
  let userCredit = 0;
  let userVouchers = 0;
  let userCompensation = 0;
  const pool = [];

  sorted.forEach((t) => {
    const amount = Number(t.amount) || 0;
    const type = t.type;
    const increment = ['credit', 'refund', 'earning', 'voucher', 'cashback'].includes(type);
    const exp = t.exp || (t.created ? moment(t.created).add(3, 'months').valueOf() : null);
    const isExpiredVoucher = moment().isAfter(moment(exp)) && type === 'voucher';
    const isExpiredBogo = moment().isAfter(moment(exp)) && type === 'bogo';

    if (isExpiredVoucher || isExpiredBogo || t.transfered || t.redeemed) return;
    if (type === 'bogo') return;

    if (['earning', 'refund', 'expense', 'credit', 'cashback'].includes(type)) {
      if (increment) {
        pool.push({ exp: { start: t.created, end: exp }, amount, id: t.id, type });
      } else {
        let balance = amount;
        const eligible = pool.filter(
          (r) => moment(t.created).isBetween(moment(r.exp.start), moment(r.exp.end))
        );
        eligible.forEach((r) => {
          if (balance >= r.amount) {
            balance -= r.amount;
            r.amount = 0;
          } else {
            r.amount -= balance;
            balance = 0;
          }
        });
      }
    }

    if (['credit', 'redeemed'].includes(type)) {
      userCredit += increment ? amount : -amount;
    }
    if (type === 'voucher') {
      if (t.description === 'Compensation') {
        userCompensation += increment ? amount : -amount;
      } else {
        userVouchers += increment ? amount : -amount;
      }
    }
  });

  const userCashback = Math.max(0, pool.reduce((total, rec) => {
    if (moment(rec.exp.end).isAfter(moment()) && !['credit'].includes(rec.type)) {
      total += rec.amount;
    }
    return total;
  }, 0));

  return {
    totalCredit: Math.max(0, userCredit),
    totalCashback: userCashback,
    totalVouchers: Math.max(0, userVouchers),
    totalCompensation: Math.max(0, userCompensation),
  };
};

// --- CalculateRedeemedTotals per user (mirrors cloud function) ---
// Every redemption is its own walletLog entry, so each total is a straight sum
// by type: `redeemed` = credit spent, `expense` = cashback spent (its amount is
// the booking's price.redeemedPoints, the same figure the CRM Orders export
// prints in its "Cashback Discount" column).
//
// This deliberately does NOT trace an `expense` back to the grant that funded
// it. The previous FIFO-pool version only counted an expense when its matching
// grant happened to fall inside the same fetched date window; since the picker
// caps the range at 30 days while cashback lives ~90, it dropped most real
// redemptions (AED 5,059.56 of 6,095.99 over 2026-08-12..09-10). The pool could
// not separate credit from cashback either — `credit` was never pushed into it,
// so its `!['credit'].includes(r.type)` guard was dead code. Log type already
// separates the two.
const calculateRedeemedForUser = (logs) => {
  let redCredit = 0;
  let redCashback = 0;

  logs.forEach((t) => {
    if (t.voided || t.transfered) return;
    const amount = Number(t.amount) || 0;
    const type = t.type;

    if (type === 'redeemed') { redCredit += amount; return; }
    if (type === 'expense') { redCashback += amount; return; }
  });

  return {
    redeemedCredit: Math.max(0, redCredit),
    redeemedCashback: Math.max(0, redCashback),
  };
};

// --- Redemption-dated totals: vouchers, compensation and BOGO ---
// These logs are written when the voucher is GRANTED and carry their own
// `redeemed` timestamp for when it was spent, so they cannot be selected by
// `created` like credit/cashback are — grant and redemption are typically days
// or months apart (p50 7d, p90 52d, max 229d in 2026-07/08 data). Feed this the
// separate `redeemed`-windowed query, not the grant-windowed one.
const calculateRedeemedVouchersForUser = (logs) => {
  let redVouchers = 0;
  let redCompensation = 0;
  let redBogoAmount = 0;

  logs.forEach((t) => {
    if (t.voided || t.transfered || !t.redeemed) return;
    const amount = Number(t.amount) || 0;
    if (t.type === 'voucher') {
      if (t.description === 'Compensation') redCompensation += amount;
      else redVouchers += amount;
      return;
    }
    if (t.type === 'bogo') redBogoAmount += Number(t.redeemedAmount) || 0;
  });

  return {
    redeemedVouchers: Math.max(0, redVouchers),
    redeemedCompensation: Math.max(0, redCompensation),
    redeemedBogoAmount: Math.max(0, redBogoAmount),
  };
};

const LoyaltyManagement = () => {
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [endDate, setEndDate] = useState(new Date());
  const [settings, setSettings] = useState({
    active: false,
    percent: 0,
    capPercent: 0,
    capValue: 0,
    expiration: 90,
    services: {},
  });
  const [transactions, setTransactions] = useState([]);
  const [voucherRedemptions, setVoucherRedemptions] = useState([]);
  const [promoDiscountByUser, setPromoDiscountByUser] = useState(new Map());
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Load settings once on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const loyaltyDoc = await firestore().collection('settings').doc('loyalty').get();
        if (loyaltyDoc.exists) {
          const data = loyaltyDoc.data();
          setSettings({
            active: data.active === true,
            percent: Number(data.percent) || 0,
            capPercent: Number(data.capPercent) || 0,
            capValue: Number(data.capValue) || 0,
            expiration: Number(data.expiration) || 90,
            services: data.services || {},
          });
        }
      } catch (error) {
        console.error('Error loading loyalty settings:', error);
        createNotification('error', 'Failed to load loyalty settings');
      }
    };
    loadSettings();
  }, []);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    if (start) setStartDate(start);
    if (end) {
      const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
      if (diffDays > 30) {
        createNotification('warning', 'Date range cannot exceed 30 days.', 'Date Range Limit');
        return;
      }
      setEndDate(end);
    } else {
      setEndDate(null);
    }
  };

  const getDateRangeDisplay = () => {
    if (startDate && endDate) {
      return `${moment(startDate).format('MMM DD, YYYY')} - ${moment(endDate).format('MMM DD, YYYY')}`;
    }
    if (startDate) return `${moment(startDate).format('MMM DD, YYYY')} - ...`;
    return 'Select date range';
  };

  // Load transactions when date range changes
  const fetchTransactions = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const startTs = moment(startDate).startOf('day').valueOf();
      const endTs = moment(endDate).endOf('day').valueOf();
      // Fetch walletLog docs (grants + redemptions) and bookings (for promoCode discount) in parallel.
      // walletLog is filtered by created (grant date); bookings by bookingDate.
      const [transactionsSnap, redemptionsSnap, bookingsSnap] = await Promise.all([
        firestore()
          .collectionGroup('walletLog')
          .where('created', '>=', startTs)
          .where('created', '<=', endTs)
          .orderBy('created', 'desc')
          .get(),
        // Vouchers/BOGO are logged at GRANT time, so a redemption that happened
        // in this window usually belongs to a doc created well before it. Select
        // those by `redeemed` instead; the grant-windowed query above still
        // drives the outstanding-balance totals.
        firestore()
          .collectionGroup('walletLog')
          .where('redeemed', '>=', startTs)
          .where('redeemed', '<=', endTs)
          .orderBy('redeemed', 'desc')
          .get(),
        firestore()
          .collection('bookings')
          .where('bookingDate', '>=', startTs)
          .where('bookingDate', '<=', endTs)
          .get(),
      ]);

      const mapDocs = (snap) => snap.docs.map((doc) => ({
        id: doc.id,
        userId: doc.ref.parent.parent.id,
        ...doc.data(),
      }));
      setTransactions(mapDocs(transactionsSnap));
      setVoucherRedemptions(mapDocs(redemptionsSnap).filter((t) => ['voucher', 'bogo'].includes(t.type)));

      // Aggregate redeemedPromoCode per customer_id over the same date window.
      // Exclude subscription bookings to match CRM Order Export scope (see CRM/orderHistory.js:478).
      const promoMap = new Map();
      bookingsSnap.docs.forEach((doc) => {
        const b = doc.data() || {};
        if (b.subscription) return;
        const uid = b.customer_id;
        const amt = Number(b?.price?.redeemedPromoCode) || 0;
        if (!uid || amt <= 0) return;
        promoMap.set(uid, (promoMap.get(uid) || 0) + amt);
      });
      setPromoDiscountByUser(promoMap);
    } catch (error) {
      console.error('Error loading loyalty transactions:', error);
      createNotification('error', 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const stats = useMemo(() => {
    const nonVoided = transactions.filter((t) => !t.voided);

    // --- Group by user first (cloud function does this) ---
    const userMap = new Map();
    nonVoided.forEach((t) => {
      if (!userMap.has(t.userId)) userMap.set(t.userId, []);
      userMap.get(t.userId).push(t);
    });

    // --- Aggregate per-user results (mirrors cloud function) ---
    const totals = {
      totalCredit: 0, totalCashback: 0, totalVouchers: 0, totalCompensation: 0,
      redeemedCredit: 0, redeemedCashback: 0, redeemedVouchers: 0,
      redeemedCompensation: 0, redeemedBogoAmount: 0,
    };

    userMap.forEach((logs) => {
      const pts = calculatePointsForUser(logs);
      const red = calculateRedeemedForUser(logs);
      totals.totalCredit += pts.totalCredit;
      totals.totalCashback += pts.totalCashback;
      totals.totalVouchers += pts.totalVouchers;
      totals.totalCompensation += pts.totalCompensation;
      totals.redeemedCredit += red.redeemedCredit;
      totals.redeemedCashback += red.redeemedCashback;
    });

    // Voucher/BOGO redemptions come from the `redeemed`-windowed query instead.
    const redeemedVoucherLogs = voucherRedemptions.filter((t) => !t.voided);
    const voucherUserMap = new Map();
    redeemedVoucherLogs.forEach((t) => {
      if (!voucherUserMap.has(t.userId)) voucherUserMap.set(t.userId, []);
      voucherUserMap.get(t.userId).push(t);
    });
    voucherUserMap.forEach((logs) => {
      const red = calculateRedeemedVouchersForUser(logs);
      totals.redeemedVouchers += red.redeemedVouchers;
      totals.redeemedCompensation += red.redeemedCompensation;
      totals.redeemedBogoAmount += red.redeemedBogoAmount;
    });

    // --- Transferred (simple sum across all) ---
    const sum = (arr) => arr.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const totalTransferred = sum(nonVoided.filter((t) => t.transfered));
    const transferredCount = nonVoided.filter((t) => t.transfered).length;

    let totalPromoCodeDiscount = 0;
    promoDiscountByUser.forEach((amt) => { totalPromoCodeDiscount += amt; });

    const total = totals.totalCashback + totals.totalCredit + totals.totalVouchers + totals.totalCompensation;

    return {
      total: Number(total).toFixed(2),
      totalCredit: Number(totals.totalCredit).toFixed(2),
      totalCashback: Number(totals.totalCashback).toFixed(2),
      totalVouchers: Number(totals.totalVouchers).toFixed(2),
      totalCompensation: Number(totals.totalCompensation).toFixed(2),
      redeemedCredit: Number(totals.redeemedCredit).toFixed(2),
      redeemedCashback: Number(totals.redeemedCashback).toFixed(2),
      redeemedVouchers: Number(totals.redeemedVouchers).toFixed(2),
      redeemedCompensation: Number(totals.redeemedCompensation).toFixed(2),
      redeemedBogoAmount: Number(totals.redeemedBogoAmount).toFixed(2),
      totalPromoCodeDiscount: Number(totalPromoCodeDiscount).toFixed(2),
      totalTransferred: Number(totalTransferred).toFixed(2),
      transferredCount,
      uniqueUsers: userMap.size,
      totalTransactions: nonVoided.length,
    };
  }, [transactions, voucherRedemptions, promoDiscountByUser]);

  const filterFns = useMemo(() => ({
    all: (t) => !t.transfered,
    credit: (t) => t.type === 'credit' && !t.transfered && !t.redeemed,
    cashback: (t) => ['earning', 'refund', 'cashback'].includes(t.type) && !t.transfered && !t.redeemed,
    voucher: (t) => t.type === 'voucher' && !t.redeemed && t.description !== 'Compensation',
    compensation: (t) => t.type === 'voucher' && !t.redeemed && t.description === 'Compensation',
    'redeemed credit': (t) => t.type === 'redeemed',
    'redeemed cashback': (t) => t.type === 'expense' && !t.transfered,
    'redeemed vouchers': (t) => t.type === 'voucher' && t.redeemed && t.description !== 'Compensation',
    'redeemed compensation': (t) => t.type === 'voucher' && t.redeemed && t.description === 'Compensation',
    bogo: (t) => t.type === 'bogo',
    transferred: (t) => !!t.transfered,
  }), []);

  const filteredTransactions = useMemo(() => {
    const nonVoided = transactions.filter((t) => !t.voided);
    const fn = filterFns[typeFilter] || filterFns.all;
    return nonVoided.filter(fn);
  }, [transactions, typeFilter, filterFns]);

  const handleViewCustomer = useCallback(async (userId) => {
    try {
      const [userDoc, ordersSnap] = await Promise.all([
        firestore().collection('users').doc(userId).get(),
        firestore()
          .collection('bookings')
          .where('customer_id', '==', userId)
          .orderBy('created', 'desc')
          .limit(50)
          .get(),
      ]);
      const userData = userDoc.exists ? userDoc.data() : {};
      const userWalletLogs = transactions.filter((t) => t.userId === userId && !t.voided);
      const orders = ordersSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setSelectedCustomer({
        id: userId,
        key: userId,
        name: userData.name || '--',
        number: userData.phoneNumber || userData.number || '--',
        email: userData.email || '--',
        phoneNumber: userData.phoneNumber || userData.number || '--',
        walletLog: userWalletLogs,
        orders,
        ordersPlaced: orders.length,
        revenue: orders.reduce((sum, o) => sum + (Number(o.price?.totalPrice) || 0), 0),
        firstOrder: orders.length > 0 ? Math.min(...orders.map((o) => o.created || 0)) : Date.now(),
        lastOrder: orders.length > 0 ? Math.max(...orders.map((o) => o.created || 0)) : Date.now(),
      });
    } catch (error) {
      console.error('Error loading customer:', error);
      createNotification('error', 'Failed to load customer data');
    }
  }, [transactions]);

  const tableColumns = useMemo(() => [
    // {
    //   Header: 'User',
    //   accessor: 'userId',
    //   Cell: ({ value }) => (
    //     <div>
    //       <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{value}</span>
    //     </div>
    //   ),
    // },
    {
      Header: 'Type',
      accessor: 'type',
      width: 200,
      Cell: ({ value }) => (
        <Badge
          color={TRANSACTION_COLORS[value] || 'secondary'}
          style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem', textTransform: 'capitalize' }}
        >
          {value}
        </Badge>
      ),
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      width: 200,
      Cell: ({ value, original }) => {
        const isDebit = ['expense', 'redeemed'].includes(original.type) || original.transfered;
        const amount = Number(Math.abs(value) || 0).toFixed(2);
        return (
          <strong style={{ color: isDebit ? '#ef4444' : '#10b981' }}>
            {isDebit ? '-' : '+'}AED {amount}
          </strong>
        );
      },
    },
    // {
    //   Header: 'Booking',
    //   accessor: 'bookingID',
    //   width: 200,
    //   Cell: ({ value }) => value ? (
    //     <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{value}</span>
    //   ) : <span className="text-muted">---</span>,
    // },
    {
      Header: 'Promocode',
      accessor: 'promoCode',
      width: 160,
      Cell: ({ value }) => value
        ? <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{value}</span>
        : <span className="text-muted">---</span>,
    },
    {
      Header: 'Date',
      accessor: 'created',
      width: 200,
      Cell: ({ value }) => value ? moment(value).format('MMM DD, YYYY HH:mm') : '---',
    },
    {
      Header: 'Expires',
      accessor: 'exp',
      // width: 130,
      Cell: ({ value }) => {
        if (!value) return <span className="text-muted">---</span>;
        const isExpired = moment(value).isBefore(moment());
        return (
          <span style={{ color: isExpired ? '#ef4444' : '#6b7280', fontSize: '0.8rem' }}>
            {moment(value).format('MMM DD, YYYY')}
            {isExpired && <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>(expired)</span>}
          </span>
        );
      },
    },
    {
      Header: 'Action',
      accessor: 'userId',
      sortable: false,
      width: 130,
      Cell: ({ value }) => (
        <Button
          color="primary"
          size="sm"
          onClick={() => handleViewCustomer(value)}
        >
          View Customer
        </Button>
      ),
    },
  ], [handleViewCustomer]);

  const exportCSV = useCallback(() => {
    const nonVoided = transactions.filter((t) => !t.voided);

    if (!nonVoided || nonVoided.length === 0) {
      createNotification('warning', 'No cashback logs data to export', 'Export Failed');
      return;
    }

    const csvRows = [];

    // Define CSV headers matching the table columns
    const headers = [
      'User ID',
      'Wallet Logs Count',
      'Wallet Total',
      'Total Credit',
      'Total Cashback',
      'Total Vouchers',
      'Total Compensation',
      'Redeemed Credit',
      'Redeemed Cashback',
      'Redeemed Vouchers',
      'Redeemed Compensation',
      'Redeemed BOGO Amount (AED)',
      'Promo Code Discount',
      'Total Transferred',
      'Transferred Count',
    ];

    // Add header row to CSV
    csvRows.push(headers.join(','));

    // Helper function to sanitize CSV fields
    const sanitizeField = (field) => {
      if (field === null || field === undefined || field === '') return '--';
      const str = String(field);
      // If field contains comma, quote, or newline, wrap in quotes and escape existing quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Group by user (mirrors cloud function behavior in walletLogs)
    const userMap = new Map();
    nonVoided.forEach((t) => {
      if (!userMap.has(t.userId)) userMap.set(t.userId, []);
      userMap.get(t.userId).push(t);
    });

    // Emit one row per user. Include users who only have promo-discount activity
    // (no walletLog entries in the window) so their promoCode discount isn't dropped.
    const voucherRedemptionsByUser = new Map();
    voucherRedemptions.filter((t) => !t.voided).forEach((t) => {
      if (!voucherRedemptionsByUser.has(t.userId)) voucherRedemptionsByUser.set(t.userId, []);
      voucherRedemptionsByUser.get(t.userId).push(t);
    });

    const allUserIds = new Set([
      ...userMap.keys(),
      ...voucherRedemptionsByUser.keys(),
      ...promoDiscountByUser.keys(),
    ]);
    allUserIds.forEach((userId) => {
      const logs = userMap.get(userId) || [];
      const pts = calculatePointsForUser(logs);
      const red = calculateRedeemedForUser(logs);
      const vred = calculateRedeemedVouchersForUser(voucherRedemptionsByUser.get(userId) || []);
      const transferredLogs = logs.filter((t) => t.transfered);
      const totalTransferred = transferredLogs.reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const transferredCount = transferredLogs.length;
      const total = pts.totalCredit + pts.totalCashback + pts.totalVouchers + pts.totalCompensation;

      const row = [
        sanitizeField(userId || '--'),
        logs.length || 0,
        Number(total).toFixed(2),
        Number(pts.totalCredit).toFixed(2),
        Number(pts.totalCashback).toFixed(2),
        Number(pts.totalVouchers).toFixed(2),
        Number(pts.totalCompensation).toFixed(2),
        Number(red.redeemedCredit).toFixed(2),
        Number(red.redeemedCashback).toFixed(2),
        Number(vred.redeemedVouchers).toFixed(2),
        Number(vred.redeemedCompensation).toFixed(2),
        Number(vred.redeemedBogoAmount).toFixed(2),
        Number(promoDiscountByUser.get(userId) || 0).toFixed(2),
        Number(totalTransferred).toFixed(2),
        transferredCount || 0,
      ];
      csvRows.push(row.join(','));
    });

    // Combine all rows into a single CSV string
    const csvString = csvRows.join('\n');

    // Create a download link for the CSV file
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString);
    const dateRange = startDate && endDate
      ? `${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format('YYYY-MM-DD')}`
      : moment().format('YYYY-MM-DD');
    link.download = `cashback_logs_${dateRange}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    createNotification('success', 'Cashback logs exported successfully', 'Export Complete');
  }, [transactions, voucherRedemptions, promoDiscountByUser, startDate, endDate]);


  if (loading) {
    return <div className="loading" />;
  }

  return (
    <div>
      {/* Header */}
      <Row>
        <Colxx xxs="5" className="mb-3">
          <h1 className="mb-2 p-0">Loyalty Program</h1>
        </Colxx>
        <Colxx xxs="7" className="mb-3">
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 12 }}>
            <Button
              color="primary"
              size="sm"
              onClick={exportCSV}
              disabled={!transactions || transactions.length === 0 || loading}
              style={{ whiteSpace: 'nowrap' }}
            >
              Export CSV
            </Button>
            <div style={{ display: 'inline-block', position: 'relative' }}>
              <DatePicker
                selected={startDate}
                onChange={handleDateRangeChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="MMM dd, yyyy"
                placeholderText="Select date range"
                maxDate={new Date()}
                filterDate={(date) => {
                  if (startDate && !endDate) {
                    const maxEnd = moment(startDate).add(30, 'days').toDate();
                    return date <= maxEnd && date <= new Date();
                  }
                  return date <= new Date();
                }}
                className="form-control"
                customInput={
                  <input
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      minWidth: '260px',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                    }}
                    value={getDateRangeDisplay()}
                    readOnly
                  />
                }
              />
            </div>
            <div
              className="px-3 py-2"
              style={{
                background: settings.active ? '#dcfce7' : '#fce4ec',
                borderRadius: 8,
              }}
            >
              <strong style={{ color: settings.active ? '#16a34a' : '#c62828', fontSize: '0.8rem' }}>
                {settings.active ? 'ACTIVE' : 'DISABLED'}
              </strong>
            </div>
          </div>
        </Colxx>
      </Row>

      {/* Stat Cards */}
      <Row className="mt-2">
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Users" value={stats.uniqueUsers} color="#3b82f6" />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Transactions" value={stats.totalTransactions} color="#10b981" />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total" value={stats.total} color="#6366f1" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Credit" value={stats.totalCredit} color="#8b5cf6" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Cashback" value={stats.totalCashback} color="#f59e0b" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Vouchers" value={stats.totalVouchers} color="#ec4899" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Compensation" value={stats.totalCompensation} color="#a855f7" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Redeemed Credit" value={stats.redeemedCredit} color="#ef4444" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Redeemed Cashback" value={stats.redeemedCashback} color="#dc2626" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Redeemed Vouchers" value={stats.redeemedVouchers} color="#b91c1c" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Redeemed Compensation" value={stats.redeemedCompensation} color="#991b1b" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Redeemed BOGO" value={stats.redeemedBogoAmount} color="#0891b2" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Promo Code Discount" value={stats.totalPromoCodeDiscount} color="#0ea5e9" formatCurrency={true} />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard title="Total Transferred" value={stats.totalTransferred} color="#2ecc71" formatCurrency={true} />
        </Colxx>
      </Row>

      {/* Transaction History */}
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card style={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
            <CardBody>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Points History</h5>
                </div>
                <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                  {Object.keys(filterFns).map((key) => {
                    const nonVoided = transactions.filter((t) => !t.voided);
                    const count = nonVoided.filter(filterFns[key]).length;
                    return (
                      <Button
                        key={key}
                        size="sm"
                        color={typeFilter === key ? 'primary' : 'light'}
                        onClick={() => setTypeFilter(key)}
                        style={{ borderRadius: 6, textTransform: 'capitalize', fontSize: '0.75rem' }}
                      >
                        {key} ({count})
                      </Button>
                    );
                  })}
                </div>
              </div>

              {filteredTransactions.length === 0 ? (
                <div className="text-center py-5">
                  <Award size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
                  <p className="text-muted mb-0">
                    {transactions.length === 0
                      ? 'No loyalty transactions yet. Points will appear once customers complete bookings.'
                      : 'No transactions match the selected filter.'}
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', width: '100%' }} className="react-table-scroll-wrapper">
                  <ReactTable
                    data={filteredTransactions}
                    columns={tableColumns}
                    defaultPageSize={10}
                    minRows={0}
                    showPageJump={false}
                    showPageSizeOptions={false}
                    PaginationComponent={DataTablePagination}
                    className="custuomReactTable"
                    defaultSorted={[{ id: 'created', desc: true }]}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Colxx>
      </Row>

      {selectedCustomer && (
        <Customer
          customer={selectedCustomer}
          dismiss={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default LoyaltyManagement;
